import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Upload,
  Calendar,
  Car,
  MapPin,
  User,
  Flag,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Cloud,
  BarChart3,
  ScatterChart,
  Edit3,
  Trash2,
  Trophy,
} from "lucide-react";
import { useSession, useDeleteSession } from "@/hooks/useSessions";
import { useLaps, useLapStatistics, useDeleteLap } from "@/hooks/useLaps";
import { useSessionConditionsManager } from "@/hooks/useSessionConditions";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import LapList from "@/components/laps/LapList";
import LapModal from "@/components/laps/LapModal";
import DataUploadModal from "@/components/sessions/DataUploadModal";
import SessionVideosSection from "@/components/sessions/SessionVideosSection";
import TelemetryFilesList from "@/components/sessions/TelemetryFilesList";
import SessionModal from "@/components/sessions/SessionModal";
import { SessionConditions } from "@/components/session/SessionConditions";
import SessionLiveStreamSection from "@/components/session/SessionLiveStreamSection";
import PageLoading from "@/components/common/PageLoading";
import PageErrorState from "@/components/common/PageErrorState";
import { VehicleModal } from "@/components/vehicles/VehicleModal";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import Modal from "@/components/ui/Modal";
import type { Lap, TelemetryFile } from "@/types";
import { handleApiError } from "@/utils/errorHandler";
import type { ApiError } from "@/utils/errorHandler";
import { AccelerationScatterPlot } from "@/components/telemetry/AccelerationScatterPlot";
import { formatSessionDateTime } from "@/utils/dateFormat";
import { formatUserName } from "@/utils/userName";

export default function SessionDetailPage() {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [telemetryFilesRefreshKey, setTelemetryFilesRefreshKey] = useState(0);
  const [selectedLap, setSelectedLap] = useState<Lap | undefined>();
  const [lapToDelete, setLapToDelete] = useState<Lap | null>(null);
  const [showConditions, setShowConditions] = useState(false);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [isSessionEditModalOpen, setIsSessionEditModalOpen] = useState(false);
  const [isDeleteSessionConfirmOpen, setIsDeleteSessionConfirmOpen] = useState(false);
  const [isScatterPlotModalOpen, setIsScatterPlotModalOpen] = useState(false);
  const [telemetryFiles, setTelemetryFiles] = useState<TelemetryFile[]>([]);
  const deleteSession = useDeleteSession();
  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError,
  } = useSession(sessionId!);
  usePageMeta({ title: session?.title, description: session?.description });
  const { data: laps = [], isLoading: lapsLoading } = useLaps(sessionId!);
  const { data: statistics } = useLapStatistics(sessionId!);
  const deleteLap = useDeleteLap();
  const conditionsManager = useSessionConditionsManager(sessionId!);

  const isSessionOwner = !!user && !!session && session.userId === user.id;

  if (!sessionId) {
    return (
 <div className="page-container">
 <div className="text-center py-12">
 <p className="text-lg text-red-600">{t("sessions.detail.invalidSessionId", { ns: "pages" })}</p>
        </div>
      </div>
    );
  }

  if (sessionLoading) {
    return (
      <div className="page-container">
        <PageLoading />
      </div>
    );
  }

  if (sessionError || !session) {
    return (
      <div className="page-container">
        <PageErrorState
          icon={AlertCircle}
          message={t("sessions.detail.loadErrorTitle", { ns: "pages" })}
          details={t("sessions.detail.loadErrorMessage", { ns: "pages" })}
          retryLabel={t("sessions.detail.backToSessions", { ns: "pages" })}
          onRetry={() => navigate("/sessions")}
        />
      </div>
    );
  }

  const handleEditLap = (lap: Lap) => {
    setSelectedLap(lap);
  };

  const handleDeleteLap = (lap: Lap) => {
    setLapToDelete(lap);
  };

  const handleConfirmDelete = async () => {
    if (!lapToDelete) return;

    try {
      await deleteLap.mutateAsync({
        sessionId: sessionId!,
        lapId: lapToDelete.id,
      });
      setLapToDelete(null);
    } catch (error) {
      handleApiError(error as ApiError, t("sessions.detail.errors.deleteLap", { ns: "pages" }));
    }
  };

  const handleCancelDelete = () => {
    setLapToDelete(null);
  };

  const handleUploadData = () => {
    setIsUploadModalOpen(true);
  };

  const handleTelemetryUploadSuccess = () => {
    setTelemetryFilesRefreshKey((current) => current + 1);
    void queryClient.invalidateQueries({
      queryKey: ["sessions", sessionId, "laps"],
    });
    void queryClient.invalidateQueries({
      queryKey: ["telemetry", "session", sessionId],
    });
  };

  const handleCloseLapModal = () => {
    setSelectedLap(undefined);
  };

  const handleVehicleClick = () => {
    setIsVehicleModalOpen(true);
  };

  const handleTrackClick = () => {
    navigate(`/tracks/${session.event.track.id}`);
  };

  const vehicleDisplayName =
    session.vehicle.name ||
    `${session.vehicle.year} ${session.vehicle.maker} ${session.vehicle.model}`;

  return (
 <div className="page-container w-full overflow-hidden">
      {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 w-full">
 <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
          <button
            onClick={() => navigate("/sessions")}
 className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors flex-shrink-0"
          >
 <ArrowLeft className="h-5 w-5" />
          </button>
 <div className="min-w-0 flex-1">
 <h1 className="page-title break-words leading-tight">
              {session.title}
            </h1>
 <p className="page-subtitle">
              {t("sessions.detail.subtitle", { ns: "pages" })}
            </p>
          </div>
        </div>

      </div>

      {/* Session Info */}
 <div className="bg-white rounded-lg border border-secondary-200 mb-8">
 <div className="p-4 sm:p-6 border-b border-secondary-100">
 <div className="flex items-center justify-between">
 <h2 className="text-lg font-semibold text-secondary-900">
              {t("sessions.detail.information.title", { ns: "pages" })}
            </h2>
           <div className="flex items-center gap-1">
            {isSessionOwner && (
              <>
                <button
                  onClick={() => setIsSessionEditModalOpen(true)}
                  className="p-1.5 text-secondary-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors flex-shrink-0"
                  title={t("sessions.detail.information.editSession", { ns: "pages" })}
                  aria-label={t("sessions.detail.information.editSession", { ns: "pages" })}
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setIsDeleteSessionConfirmOpen(true)}
                  className="p-1.5 text-secondary-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                  title={t("sessions.detail.deleteSession", { ns: "pages" })}
                  aria-label={t("sessions.detail.deleteSession", { ns: "pages" })}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
          </div>
        </div>
 <div className="p-4 sm:p-6">
 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
 <div className="flex items-center space-x-3 min-w-0">
 <Calendar className="h-5 w-5 text-secondary-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm text-secondary-500">{t("sessions.detail.information.fields.date", { ns: "pages" })}</div>
 <div className="font-medium text-secondary-900 truncate">
                  {formatSessionDateTime(session.conductedAt, i18n.language)}
                </div>
              </div>
            </div>

 <div className="flex items-center space-x-3 min-w-0">
 <User className="h-5 w-5 text-secondary-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm text-secondary-500">{t("sessions.detail.information.fields.driver", { ns: "pages" })}</div>
 <div className="font-medium text-secondary-900 truncate">
                  {formatUserName(session.user)}
                </div>
              </div>
            </div>

 <div className="flex items-center space-x-3 min-w-0">
 <Car className="h-5 w-5 text-secondary-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm text-secondary-500">{t("sessions.detail.information.fields.vehicle", { ns: "pages" })}</div>
                <div
 className="font-medium text-secondary-900 cursor-pointer hover:text-primary-600 hover:underline transition-colors truncate"
                  onClick={handleVehicleClick}
                  title={vehicleDisplayName}
                >
                  {vehicleDisplayName}
                </div>
              </div>
            </div>

 <div className="flex items-center space-x-3 min-w-0">
 <Flag className="h-5 w-5 text-secondary-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm text-secondary-500">{t("sessions.detail.information.fields.event", { ns: "pages" })}</div>
                <div
 className="font-medium text-secondary-900 truncate"
                  title={session.event.name}
                >
                  {session.event.name}
                </div>
              </div>
            </div>

 <div className="flex items-center space-x-3 min-w-0">
 <MapPin className="h-5 w-5 text-secondary-400 flex-shrink-0" />
 <div className="min-w-0">
 <div className="text-sm text-secondary-500">{t("sessions.detail.information.fields.track", { ns: "pages" })}</div>
                <div
 className="font-medium text-secondary-900 cursor-pointer hover:text-primary-600 hover:underline transition-colors truncate"
                  onClick={handleTrackClick}
                  title={session.event.track.name}
                >
                  {session.event.track.name}
                </div>
              </div>
            </div>

            {session.leaderboardOptIn && (
              <div className="flex items-center space-x-3 min-w-0">
                <Trophy className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-secondary-500">
                    {t("sessions.detail.information.fields.leaderboard", { ns: "pages" })}
                  </div>
                  <div className="font-medium text-secondary-900 truncate">
                    {t("sessions.detail.information.fields.leaderboardOptedIn", { ns: "pages" })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {session.description && (
 <div className="mt-4 pt-4 border-t border-secondary-100">
 <div className="text-sm text-secondary-500 mb-2">
                {t("sessions.detail.information.fields.description", { ns: "pages" })}
              </div>
 <p className="text-secondary-700 bg-secondary-50 rounded p-3 break-words">
                {session.description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Session Conditions */}
 <div className="bg-white rounded-lg border border-secondary-200 mb-8">
 <div className="p-4 sm:p-6 border-b border-secondary-100">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <h3 className="text-lg font-semibold text-secondary-900">
              {t("sessions.detail.conditions.title", { ns: "pages" })}
            </h3>
            <button
              onClick={() => setShowConditions(!showConditions)}
 className={`btn-secondary px-4 py-2 flex items-center space-x-2 self-start sm:self-center whitespace-nowrap ${showConditions ? "bg-primary-50 border-primary-200 text-primary-700" : ""}`}
            >
 <Cloud className="h-4 w-4" />
              <span>{t("sessions.detail.conditions.button", { ns: "pages" })}</span>
              {showConditions ? (
 <ChevronUp className="h-4 w-4" />
              ) : (
 <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>
        {showConditions && session && (
 <div className="p-4 sm:p-6">
            <SessionConditions
              session={session}
              onSave={conditionsManager.updateConditions}
              isSaving={conditionsManager.isUpdating}
              error={conditionsManager.error}
            />
          </div>
        )}
      </div>

      {/* Live Stream */}
      <SessionLiveStreamSection
        session={session}
        sessionId={sessionId}
        isOwner={isSessionOwner}
      />

      <SessionVideosSection session={session} />

      {/* Telemetry Data */}
 <div className="bg-white rounded-lg border border-secondary-200 mb-8">
 <div className="p-4 sm:p-6 border-b border-secondary-100">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <h3 className="text-lg font-semibold text-secondary-900">
              {t("sessions.detail.telemetry.title", { ns: "pages" })}
            </h3>
 <div className="flex flex-col sm:flex-row gap-2 self-start sm:self-center">
              {isSessionOwner && (
                <button
                  onClick={handleUploadData}
                  className="btn-secondary px-4 py-2 flex items-center space-x-2 whitespace-nowrap"
                >
                  <Upload className="h-4 w-4" />
                  <span>{t("sessions.detail.telemetry.upload", { ns: "pages" })}</span>
                </button>
              )}
              <button
                onClick={() => setIsScatterPlotModalOpen(true)}
 className="btn-secondary px-4 py-2 flex items-center space-x-2 whitespace-nowrap"
              >
 <ScatterChart className="h-4 w-4" />
                <span>{t("sessions.detail.telemetry.scatterPlot", { ns: "pages" })}</span>
              </button>
              <button
                onClick={() => navigate(`/sessions/${sessionId}/analytics`)}
 className="btn-primary px-4 py-2 flex items-center space-x-2 whitespace-nowrap"
              >
 <BarChart3 className="h-4 w-4" />
                <span>{t("sessions.detail.telemetry.openAnalytics", { ns: "pages" })}</span>
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 border-b border-secondary-100">
          <TelemetryFilesList
            sessionId={sessionId}
            onFilesChange={setTelemetryFiles}
            canManage={isSessionOwner}
            refreshKey={telemetryFilesRefreshKey}
          />
        </div>

        {telemetryFiles.length > 0 && (
 <div className="p-4 sm:p-6">
 <div className="text-center py-8">
 <BarChart3 className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
 <p className="text-lg font-medium text-secondary-600 mb-2">
              {t("sessions.detail.telemetry.analysisTitle", { ns: "pages" })}
            </p>
 <p className="text-secondary-500 mb-4">
              {t("sessions.detail.telemetry.analysisDescription", { ns: "pages" })}
            </p>
            <button
              onClick={() => navigate(`/sessions/${sessionId}/analytics`)}
 className="btn-primary px-6 py-3 flex items-center space-x-2 mx-auto"
            >
 <BarChart3 className="h-5 w-5" />
              <span>{t("sessions.detail.telemetry.openAnalyticsDashboard", { ns: "pages" })}</span>
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Laps */}
 <div className="bg-white rounded-lg border border-secondary-200 mb-8">
 <div className="p-4 sm:p-6 border-b border-secondary-100">
 <h3 className="text-lg font-semibold text-secondary-900">{t("sessions.detail.laps.title", { ns: "pages" })}</h3>
        </div>
 <div className="p-4 sm:p-6">
          {/* Lap List */}
          <LapList
            laps={laps}
            statistics={statistics}
            isLoading={lapsLoading}
            onEdit={isSessionOwner ? handleEditLap : undefined}
            onDelete={
              isSessionOwner
                ? (lapId: string) => {
                    const lap = laps.find((l) => l.id === lapId);
                    if (lap) handleDeleteLap(lap);
                  }
                : undefined
            }
          />
        </div>
      </div>

      {/* Modals */}
      {selectedLap && (
        <LapModal
          isOpen
          onClose={handleCloseLapModal}
          lap={selectedLap}
        />
      )}

      <DataUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        session={session}
        onUploadSuccess={handleTelemetryUploadSuccess}
      />

      <VehicleModal
        isOpen={isVehicleModalOpen}
        onClose={() => setIsVehicleModalOpen(false)}
        vehicle={session.vehicle}
        mode="view"
      />

      <SessionModal
        isOpen={isSessionEditModalOpen}
        onClose={() => setIsSessionEditModalOpen(false)}
        session={session}
      />

      <Modal
        isOpen={isScatterPlotModalOpen}
        onClose={() => setIsScatterPlotModalOpen(false)}
        title={t("sessions.detail.telemetry.scatterPlot", { ns: "pages" })}
        size="xl"
      >
        <AccelerationScatterPlot session={session} />
      </Modal>

      <ConfirmationModal
        isOpen={!!lapToDelete}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t("sessions.detail.laps.deleteTitle", { ns: "pages" })}
        message={
          lapToDelete
            ? t("sessions.detail.laps.deleteMessage", {
              ns: "pages",
              lapNumber: lapToDelete.lapNumber,
            })
            : ""
        }
        confirmButtonText={t("common:delete")}
        confirmButtonColor="red"
        isLoading={deleteLap.isPending}
      />

      <ConfirmationModal
        isOpen={isDeleteSessionConfirmOpen}
        onClose={() => setIsDeleteSessionConfirmOpen(false)}
        onConfirm={async () => {
          try {
            await deleteSession.mutateAsync(sessionId!);
            setIsDeleteSessionConfirmOpen(false);
            navigate("/sessions");
          } catch (error) {
            handleApiError(error as ApiError, t("sessions.detail.errors.deleteSession", { ns: "pages" }));
          }
        }}
        title={t("sessions.detail.deleteSessionTitle", { ns: "pages" })}
        message={t("sessions.detail.deleteSessionMessage", { ns: "pages" })}
        confirmButtonText={t("common:delete")}
        confirmButtonColor="red"
        isLoading={deleteSession.isPending}
      />
    </div>
  );
}
